import Modal from '@/src/Common/components/Modal';
import useShare from '@/src/Share/components/hooks/useShare';
import { PlusIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';

export default function AddSessionShareModal({
    photoSessionId,
    shareIdentifier,
    activationId,
    galleryId,
    onComplete,
}: {
    photoSessionId: string;
    shareIdentifier: string;
    activationId: number;
    galleryId: number;
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const [newDestination, setNewDestination] = useState('');
    const [isAddingShare, setIsAddingShare] = useState(false);
    const { addShareToSession } = useShare();

    const addShare = async () => {
        if (!newDestination) {
            return;
        }

        setIsAddingShare(true);
        await addShareToSession(
            photoSessionId,
            shareIdentifier,
            newDestination,
            activationId,
            galleryId,
        );
        setIsAddingShare(false);
        toast.success('Share added to session');
        onComplete();
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onComplete}>
            <div>
                <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0 sm:h-10 sm:w-10">
                            <PlusIcon
                                className="h-6 w-6 text-primary-foreground"
                                aria-hidden="true"
                            />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                            <h3
                                className="text-base font-semibold text-foreground"
                            >
                                Add Share To Session
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-muted-foreground">
                                    Please provide an email address or phone
                                    number to share this session with. Shares
                                    will be immediately sent.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ml-12 mt-2 p-2">
                    <div className="space-y-2">
                        <Label htmlFor="destination">
                            Email / Phone Number
                        </Label>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={newDestination}
                            onChange={(e) =>
                                setNewDestination(e.target.value)
                            }
                            placeholder="Enter email or phone number"
                        />
                    </div>
                </div>
            </div>
            <div className="bg-muted/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                <Button
                    onClick={addShare}
                    disabled={isAddingShare}
                >
                    {isAddingShare ? 'Adding...' : 'Add Share'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onComplete()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </Button>
            </div>
        </Modal>
    );
}
